import React, { useState, useEffect } from "react";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Plus, Edit, Trash2, ShoppingCart, Package, Calendar, DollarSign, CheckCircle } from "lucide-react";
import { usePermissions } from "../contexts/PermissionsContext";

const PurchaseOrdersPage = () => {
  const { can } = usePermissions();
  const [pos, setPos] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editPO, setEditPO] = useState(null);
  const [selectedProject, setSelectedProject] = useState("all");
  const [form, setForm] = useState({
    project_id: "",
    vendor: "",
    items: [{ description: "", quantity: 1, unit_price: 0, received: false }],
    amount: "",
    status: "draft",
    delivery_date: "",
    notes: "",
  });

  const fetchData = async () => {
    try {
      const [posRes, projectsRes] = await Promise.all([
        API.get("/purchase-orders"),
        API.get("/projects"),
      ]);
      setPos(posRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      toast.error("Failed to load purchase orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const calculateTotal = (items) => {
    return items.reduce((sum, item) => sum + item.quantity * item.unit_price, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...form,
        amount: calculateTotal(form.items),
      };

      if (editPO) {
        await API.put(`/purchase-orders/${editPO.po_id}`, data);
        toast.success("Purchase order updated");
      } else {
        await API.post("/purchase-orders", data);
        toast.success("Purchase order created");
      }
      setDialogOpen(false);
      setEditPO(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Failed to save purchase order");
    }
  };

  const resetForm = () => {
    setForm({
      project_id: "",
      vendor: "",
      items: [{ description: "", quantity: 1, unit_price: 0, received: false }],
      amount: "",
      status: "draft",
      delivery_date: "",
      notes: "",
    });
  };

  const handleEdit = (po) => {
    setEditPO(po);
    setForm({
      project_id: po.project_id,
      vendor: po.vendor,
      items: po.items?.length > 0 ? po.items : [{ description: "", quantity: 1, unit_price: 0, received: false }],
      amount: po.amount?.toString() || "",
      status: po.status,
      delivery_date: po.delivery_date || "",
      notes: po.notes || "",
    });
    setDialogOpen(true);
  };

  const handleDelete = async (poId) => {
    if (!window.confirm("Delete this purchase order?")) return;
    try {
      await API.delete(`/purchase-orders/${poId}`);
      toast.success("Purchase order deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete purchase order");
    }
  };

  const handleItemReceived = async (po, itemIndex, received) => {
    const updatedItems = [...po.items];
    updatedItems[itemIndex] = { ...updatedItems[itemIndex], received };
    try {
      await API.put(`/purchase-orders/${po.po_id}`, { items: updatedItems });
      toast.success("Item updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update item");
    }
  };

  const addItem = () => {
    setForm({
      ...form,
      items: [...form.items, { description: "", quantity: 1, unit_price: 0, received: false }],
    });
  };

  const removeItem = (index) => {
    setForm({
      ...form,
      items: form.items.filter((_, i) => i !== index),
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...form.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setForm({ ...form, items: newItems });
  };

  const getProjectName = (projectId) => {
    const project = projects.find((p) => p.project_id === projectId);
    return project?.name || "Unknown";
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: "bg-slate-500/20 text-slate-400 border border-slate-500/30",
      sent: "bg-blue-500/20 text-blue-400 border border-blue-500/30",
      received: "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30",
      closed: "bg-slate-600/20 text-slate-300 border border-slate-600/30",
    };
    return <span className={`px-2 py-1 rounded text-xs font-semibold ${styles[status] || "bg-slate-500/20"}`}>{status}</span>;
  };

  const filteredPOs = selectedProject && selectedProject !== "all"
    ? pos.filter((po) => po.project_id === selectedProject)
    : pos;

  if (loading) {
    return <div className="animate-pulse text-slate-400">Loading purchase orders...</div>;
  }

  return (
    <div data-testid="purchase-orders-page" className="space-y-6 animate-slide-in">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-chivo font-bold text-white tracking-tight">Purchase Orders</h1>
          <p className="text-slate-400 font-ibm mt-1">Manage vendor orders and materials</p>
        </div>
        <div className="flex items-center gap-4">
          <Select value={selectedProject} onValueChange={setSelectedProject}>
            <SelectTrigger className="w-48 bg-slate-900 border-slate-700 text-slate-300" data-testid="filter-project-select">
              <SelectValue placeholder="All Projects" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="all" className="text-slate-200">All Projects</SelectItem>
              {projects.map((project) => (
                <SelectItem key={project.project_id} value={project.project_id} className="text-slate-200">
                  {project.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {can('create_purchase_order') && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  data-testid="add-po-btn"
                  onClick={() => {
                    setEditPO(null);
                    resetForm();
                  }}
                  className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold"
                >
                  <Plus size={18} className="mr-2" />
                  New PO
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-900 border-slate-800">
              <DialogHeader>
                <DialogTitle className="font-chivo text-white">{editPO ? "Edit Purchase Order" : "New Purchase Order"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-400">Project *</label>
                    <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                      <SelectTrigger data-testid="po-project-select" className="bg-slate-800 border-slate-700 text-white">
                        <SelectValue placeholder="Select project" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        {projects.map((project) => (
                          <SelectItem key={project.project_id} value={project.project_id} className="text-slate-200">
                            {project.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-400">Vendor *</label>
                    <Input
                      data-testid="po-vendor-input"
                      value={form.vendor}
                      onChange={(e) => setForm({ ...form, vendor: e.target.value })}
                      required
                      placeholder="Vendor name"
                      className="bg-slate-800 border-slate-700 text-white placeholder-slate-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-400 mb-2 block">Items</label>
                  <div className="space-y-2">
                    {form.items.map((item, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Input
                          className="flex-1 bg-slate-800 border-slate-700 text-white placeholder-slate-500"
                          value={item.description}
                          onChange={(e) => updateItem(index, "description", e.target.value)}
                          placeholder="Item description"
                        />
                        <Input
                          className="w-20 bg-slate-800 border-slate-700 text-white"
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, "quantity", parseInt(e.target.value) || 1)}
                          min="1"
                        />
                        <Input
                          className="w-28 bg-slate-800 border-slate-700 text-white"
                          type="number"
                          value={item.unit_price}
                          onChange={(e) => updateItem(index, "unit_price", parseFloat(e.target.value) || 0)}
                          placeholder="Price"
                          step="0.01"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeItem(index)}
                          disabled={form.items.length === 1}
                          className="text-slate-400 hover:text-red-400"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={addItem} className="mt-2 border-slate-700 text-slate-300 hover:bg-slate-800">
                    <Plus size={14} className="mr-1" />
                    Add Item
                  </Button>
                  <div className="mt-2 text-right">
                    <span className="text-sm text-slate-500">Total: </span>
                    <span className="font-semibold text-white">${calculateTotal(form.items).toFixed(2)}</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-400">Delivery Date</label>
                    <Input
                      type="date"
                      data-testid="po-delivery-date"
                      value={form.delivery_date}
                      onChange={(e) => setForm({ ...form, delivery_date: e.target.value })}
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-400">Status</label>
                    <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                      <SelectTrigger data-testid="po-status-select" className="bg-slate-800 border-slate-700 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="draft" className="text-slate-200">Draft</SelectItem>
                        <SelectItem value="sent" className="text-slate-200">Sent</SelectItem>
                        <SelectItem value="received" className="text-slate-200">Received</SelectItem>
                        <SelectItem value="closed" className="text-slate-200">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-400">Notes</label>
                  <textarea
                    data-testid="po-notes-input"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-md text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                    rows={2}
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-700 text-slate-300 hover:bg-slate-800">
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="save-po-btn" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">
                    {editPO ? "Update" : "Create"} PO
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      {/* PO List */}
      {filteredPOs.length === 0 ? (
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-12 text-center">
          <ShoppingCart size={48} className="mx-auto text-slate-600 mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">No purchase orders yet</h3>
          <p className="text-slate-500 mb-4">Create your first purchase order to track materials</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredPOs.map((po) => (
            <div
              key={po.po_id}
              data-testid={`po-card-${po.po_id}`}
              className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden hover:border-slate-700 transition-colors"
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="font-chivo font-semibold text-lg text-white">{po.vendor}</h3>
                      {getStatusBadge(po.status)}
                    </div>
                    <p className="text-sm text-slate-500">{getProjectName(po.project_id)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid={`edit-po-${po.po_id}`}
                      onClick={() => handleEdit(po)}
                      className="border-slate-700 text-slate-300 hover:bg-slate-800"
                    >
                      <Edit size={14} className="mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid={`delete-po-${po.po_id}`}
                      onClick={() => handleDelete(po.po_id)}
                      className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>

                <div className="flex items-center gap-6 text-sm text-slate-400 mb-4">
                  <div className="flex items-center gap-2">
                    <DollarSign size={16} className="text-slate-500" />
                    <span className="text-white font-medium">${po.amount?.toLocaleString() || 0}</span>
                  </div>
                  {po.delivery_date && (
                    <div className="flex items-center gap-2">
                      <Calendar size={16} className="text-slate-500" />
                      <span>Due: {po.delivery_date}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Package size={16} className="text-slate-500" />
                    <span>{po.items?.length || 0} items</span>
                  </div>
                </div>

                {/* Materials Checklist */}
                {po.items?.length > 0 && (
                  <div className="border-t border-slate-800 pt-4">
                    <h4 className="text-sm font-medium text-slate-400 mb-3">Materials Received Checklist</h4>
                    <div className="space-y-2">
                      {po.items.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-2 bg-slate-800 rounded-md border border-slate-700">
                          <Checkbox
                            checked={item.received}
                            onCheckedChange={(checked) => handleItemReceived(po, idx, checked)}
                            data-testid={`item-checkbox-${po.po_id}-${idx}`}
                          />
                          <span className={`flex-1 text-sm ${item.received ? "line-through text-slate-500" : "text-slate-300"}`}>
                            {item.description || "Item"} (x{item.quantity})
                          </span>
                          <span className="text-sm text-slate-500">${(item.quantity * item.unit_price).toFixed(2)}</span>
                          {item.received && <CheckCircle size={14} className="text-emerald-500" />}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PurchaseOrdersPage;
